import { useMemo, useState, type FormEvent } from 'react';
import { ApiError, completeClearanceBasis } from '../api';
import {
  basisCompletionDto,
  basisCompletionRefusal,
  basisDateHint,
  emptyBasisDraft,
  isoDay,
  type BasisCompletionDraft,
} from './clearance-entry';
import { cardDate } from './worker-card';
import { localToday } from './personnel-rows';

// Дозаполнение основания (issue #537, уточнение Штаба 29.09.2026).
//
// НЕ ИСПРАВЛЕНИЕ. Номер и дату документа впервые вписывают в действующую
// запись, внесённую до того, как они стали обязательными. Такой документ почти
// всегда старше окна организации, поэтому окна здесь нет: дата лишь не в
// будущем и не позже начала действия допуска. После дозаполнения запись
// правится обычным «Исправить» со всеми обычными правилами.
//
// Проверки здесь — повторение серверных (`clearanceBasisCompletionRefusal`),
// последнее слово за сервером: его отказ показывается как есть.

export function ClearanceBasisForm({
  clearanceId,
  typeName,
  validFrom,
  token,
  organizationId,
  onDone,
  onCancel,
  onUnauthorized,
}: {
  clearanceId: string;
  typeName: string;
  /** Начало действия допуска, `ГГГГ-ММ-ДД`: дата документа не может быть позже. */
  validFrom: string;
  token: string;
  organizationId: string;
  onDone: () => void;
  onCancel: () => void;
  onUnauthorized: () => void;
}) {
  const [draft, setDraft] = useState<BasisCompletionDraft>(emptyBasisDraft);
  const [serverRefusal, setServerRefusal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const today = useMemo(() => isoDay(localToday()), []);
  const latestAllowed = validFrom.slice(0, 10) < today ? validFrom.slice(0, 10) : today;
  const refusal = basisCompletionRefusal(draft, validFrom, today);

  const change = (field: keyof BasisCompletionDraft) => (value: string) =>
    setDraft((previous) => ({ ...previous, [field]: value }));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (refusal !== null || busy) return;
    setBusy(true);
    setServerRefusal(null);
    try {
      await completeClearanceBasis(clearanceId, basisCompletionDto(draft), token, organizationId);
      onDone();
    } catch (error: unknown) {
      if (error instanceof ApiError && error.status === 401) onUnauthorized();
      setServerRefusal(error instanceof Error ? error.message : 'Основание не сохранено.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="clearance-entry clearance-basis" onSubmit={submit}>
      <h3 className="worker-card__title">Дозаполнить основание: {typeName}</h3>
      <p className="section__note">
        Допуск действует с {cardDate(validFrom)}. Впишите номер и дату документа, по которому он был выдан.
      </p>

      <label className="clearance-entry__field">
        <span className="issue__label">Номер документа-основания</span>
        <input
          className="ui-input"
          value={draft.documentNumber}
          onChange={(event) => change('documentNumber')(event.target.value)}
          disabled={busy}
        />
      </label>

      <label className="clearance-entry__field">
        <span className="issue__label">Дата документа</span>
        <input
          className="ui-input"
          type="date"
          value={draft.documentDate}
          max={latestAllowed}
          onChange={(event) => change('documentDate')(event.target.value)}
          disabled={busy}
        />
        <span className="issue__hint">{basisDateHint(validFrom)}</span>
      </label>

      <label className="clearance-entry__field">
        <span className="issue__label">Причина</span>
        <textarea
          className="ui-input issue__area"
          value={draft.reason}
          onChange={(event) => change('reason')(event.target.value)}
          disabled={busy}
        />
        <span className="issue__hint">Откуда взялись номер и дата — останется в истории допусков работника.</span>
      </label>

      {serverRefusal !== null ? <p className="ui-error">{serverRefusal}</p> : null}
      {refusal !== null ? <p className="section__note section__note--warn">{refusal}</p> : null}

      <div className="clearance-entry__actions">
        <button type="submit" className="ui-btn ui-btn--primary" disabled={refusal !== null || busy}>
          {busy ? 'Сохраняю…' : 'Сохранить основание'}
        </button>
        <button type="button" className="ui-btn ui-btn--ghost" onClick={onCancel} disabled={busy}>
          Отмена
        </button>
      </div>
    </form>
  );
}
